import { NewApplicationStatus, NewApplicationStatusResponse } from '../types/responses/NewApplicationStatus';
import { ApplicationStatus, ApplicationStatusResponse } from '../types/responses/ApplicationStatus';

export class ApplicationStatusMapper {
    static mapNewToOldFormat(newResponse: NewApplicationStatusResponse): ApplicationStatusResponse {
        if (!newResponse?.applications || !Array.isArray(newResponse.applications)) {
            return {
                BearerToken: { access_token: null },
                ApplicationStatus: [],
            };
        }

        const mappedApplications: ApplicationStatus[] = newResponse.applications.map((app: NewApplicationStatus) => {
            const status = this.mapNewStatusToLegacyStatus(
                app.applicationStatus.applicationStatus, 
                app.applicationStatus.statusDescription,
            );

            const verificationStatus = this.mapFundingAttemptsToVerificationStatus(
                app.applicationStatus.applicationStatus, 
                app.applicationStatus.statusDescription, 
            );

            return {
                ApplicationId: app.applicationId,
                ProductName: app.card?.name || 'Credit Card',
                ReservationNumber: app.reservationNumber || '',
                Status: status,
                ReceivedDate: app.receivedDateTime ?new Date(app.receivedDateTime).toISOString() : '',
                MailDate: app.cardShippingInfo?.cardShippedDate ?new Date(app.cardShippingInfo?.cardDeliveryDate).toISOString() : '',
                ArrivalDate: app.cardShippingInfo?.cardDeliveryDate ?new Date(app.cardShippingInfo?.cardDeliveryDate).toISOString() : '',
                WasCardMailed: app.cardShippingInfo?.cardShipped || false,
                IsNotEnrolledInCreditProtectionOffer: !app.enrollments?.creditProtectionEnrolled || false,
                BankAccountVerificationStatus: verificationStatus,
                LastFourBankAccountNumbers: app.lastFourBankAccountNumbers || '', // this doesn't exist for new api and will likely go away
                applicationStatus: app.applicationStatus?.applicationStatus,
                statusDescription: app.applicationStatus?.statusDescription,
                fundingAttempts: app.fundingAttempts,
                rawApplicationData: app,
            } as ApplicationStatus;
        });

        return {
            //will need to be handled separately if required
            BearerToken: { access_token: null },
            ApplicationStatus: mappedApplications,
        };
    }

    private static mapNewStatusToLegacyStatus(status: string, statusDescription: string): string {
        if (status?.toLowerCase()?.includes('ready to fund') || statusDescription?.toLowerCase()?.includes('ready to fund')) {
            return 'Ready to Fund';
        }

        if (status?.toLowerCase()?.includes('approved')) {
            return 'Approved';
        }

        if (status?.toLocaleLowerCase()?.includes('pending') || status?.toLowerCase()?.includes('softdecline') || status?.toLowerCase()?.includes('error')) {
            return 'In Process';
        }

        if (status?.toLocaleLowerCase()?.includes('missing information')) {
            return 'Missing Information';
        }

        if (status?.toLowerCase()?.includes('declined') || status?.toLowerCase()?.includes('harddecline')) {
            return 'Declined';
        }

        if (status?.toLocaleLowerCase()?.includes('cancelled')) {
            return 'Cancelled';
        }
    }

    private static mapFundingAttemptsToVerificationStatus(status: string, statusDescription: string): string {
        if (status?.toLowerCase()?.includes('ready to fund') || statusDescription?.toLowerCase()?.includes('ready to fund')) {
            return 'ReadyToFund';
        }

        // All other verificationStatus' either do not exist in new api or 
        // prefer status value.
        return 'NotApplicable';
    }

    static shouldRedirectToPendingDecision(application: NewApplicationStatus): boolean {
        return application.fundingAttempts?.achAttemptsExceeded && 
            application.fundingAttempts?.debitAttemptsExceeded &&
            !application.applicationStatus?.applicationStatus?.toLowerCase()?.includes('declined');
    }

    static getAvailableFundingMethods(application: NewApplicationStatus): {
        achAvailable: boolean;
        debitAvailable: boolean;
    } {
        return {
            achAvailable: !application.fundingAttempts?.achAttemptsExceeded,
            debitAvailable: !application.fundingAttempts?.debitAttemptsExceeded,
        };
    }

    static hasRemainingAttempts(application: NewApplicationStatus): boolean {
        const available = this.getAvailableFundingMethods(application);
        return available.achAvailable || available.debitAvailable;
    }

    static buildFlowApplicationDataFromStatus = (applicationData: NewApplicationStatus): Record<string, any> => {
        const flowApplicationData: any = { ...applicationData };
    
        flowApplicationData.programCode = applicationData.card?.keyCardFeatures?.campaign?.program?.programCode;
        flowApplicationData.offerSecuredCard = applicationData.card?.keyCardFeatures?.campaign?.program?.cardType === 'SECURED_CARDS';
        flowApplicationData.IsSecuredCard = flowApplicationData.offerSecuredCard;
        flowApplicationData.IsMailingHomeAddressSame = applicationData.parties?.[0]?.addresses?.length === 1;
    
        return flowApplicationData;
    };

}
